import { useEffect } from "react";
import { Layout } from "@/components/layout";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";
import { container } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { TemplatesEmpty } from "@/components/ui/templates/empty-templates";
import { TemplatesList } from "@/components/ui/templates/list-templates";
import { useTemplateStore } from "@/store/templates.store";
import { useNavigate } from "@tanstack/react-router";
import { useAuthStore } from "@/store/auth.store";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { ValidationSuscriptionButton } from "@/common/enums/enums";

export const Templates = (): JSX.Element => {
  const { t } = useTranslation();
  const { loading, templates, fetchAndSetTemplates } = useTemplateStore();
  const { userData } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (userData?.id) {
      void fetchAndSetTemplates({ userId: userData.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout
      navbarTitle={t("templates.title")}
      breadcrumbs={[
        { name: t("templates.title"), href: `/templates`, current: true },
      ]}
    >
      <div className="h-full bg-background p-6">
        {templates.length === 0 && !loading && <TemplatesEmpty />}

        {loading && (
          <div className="flex justify-center mt-20">
            <LoadingSpinner size={8} />
          </div>
        )}

        {!!templates.length && (
          <motion.div
            animate="show"
            className="mx-auto max-w-5xl space-y-8"
            initial="hidden"
            variants={container}
          >
            <div className="p-4">
              <div className="flex justify-end mb-6">
                <div className="flex items-center">
                  <SubscriptionOnlyButton
                    validations={[ValidationSuscriptionButton.PLAN_FREE_TRIAL]}
                    onClick={async () => {
                      await navigate({ to: "/template/new" });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("templates.newTemplateButton")}
                  </SubscriptionOnlyButton>
                </div>
              </div>
              <TemplatesList templates={templates} />
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};
